import { Outlet } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuth } from './hooks/useAuth';
import { appConfig, isDevelopment } from './lib/env';
import { buildInfo } from './lib/build-info';
import { ThemeToggleIcon, ThemeStatus } from './components/ThemeToggle';
import { MobileNav } from './components/MobileNav';
import { DevTools } from './components/DevTools';
import { AppLayout } from './components/layout/AppLayout';

function App() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-background text-foreground theme-transition">
      {/* Development status indicator */}
      {isDevelopment && (
        <div className="bg-warning-100 text-warning-800 px-4 py-2 text-sm border-b border-warning-200 dark:bg-warning-900/20 dark:text-warning-300 dark:border-warning-800/30">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <span>
              🔧 {appConfig.environment} - {appConfig.app.name}
            </span>
            <div className="flex items-center gap-3">
              <ThemeStatus />
              <ThemeToggleIcon size="sm" />
            </div>
          </div>
        </div>
      )}

      <AppLayout>
        <main className="flex-1 lg:pb-0 pb-16">
          {/* Router outlet for all pages */}
          <Outlet />
        </main>
      </AppLayout>

      {/* Mobile bottom navigation */}
      <MobileNav />

      {/* Development tools */}
      <DevTools />

      {/* Toast notifications */}
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'hsl(var(--card))',
            color: 'hsl(var(--card-foreground))',
            border: '1px solid hsl(var(--border))',
            borderRadius: '0.5rem',
            padding: '1rem',
          },
          success: {
            iconTheme: {
              primary: 'hsl(var(--success))',
              secondary: 'hsl(var(--success-foreground))',
            },
          },
          error: {
            iconTheme: {
              primary: 'hsl(var(--destructive))',
              secondary: 'hsl(var(--destructive-foreground))',
            },
          },
        }}
      />

      {/* Development integration status (only in dev) */}
      {isDevelopment && (
        <footer className="bg-muted border-t mt-auto">
          <div className="max-w-7xl mx-auto p-6">
            {/*
              The framework checklist that used to live here ("React Router
              v6 — routing complete", "Zod v3 — schema validation working")
              was scaffolding rendered to end users. Two independent
              usability reviews of the admin screens, given no prompting,
              both named it the most out-of-place thing on the page: it
              means nothing to an admin, cost ~700px under every view, and
              made the tool read as unfinished. The build stamp below stays
              — it is how a deploy gets verified.
            */}
            <div className="space-y-4">
              {user && (
                <div className="pt-4 border-t border-border">
                  <div className="text-sm text-muted-foreground">
                    <strong className="text-foreground">User:</strong>{' '}
                    {user.display_name} ({user.email})
                  </div>
                </div>
              )}
              <div className="pt-4 border-t border-border">
                <div className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Build:</strong>{' '}
                  {buildInfo.shortCommit} @{' '}
                  {new Date(buildInfo.buildTime).toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}

export default App;
